import React from 'react';

/**
 * 通知バナーのタイプ
 */
export type NotificationType = 'success' | 'error' | 'info' | 'warning';

/**
 * 通知バナーのプロパティ
 */
export interface NotificationBannerProps {
  /**
   * 通知タイプ（色とアイコンが変わる）
   */
  readonly type: NotificationType;

  /**
   * 通知メッセージ
   */
  readonly message: string;

  /**
   * 見出しテキスト（オプション）
   * @example "確認完了"
   */
  readonly title?: string;

  /**
   * 閉じるボタンの表示フラグ
   * @default false
   */
  readonly dismissible?: boolean;

  /**
   * 閉じるボタンクリック時のハンドラー
   */
  readonly onDismiss?: () => void;

  /**
   * 追加の CSS クラス
   */
  readonly className?: string;
}

/**
 * 通知タイプに応じたスタイルを返す
 */
function getNotificationStyles(type: NotificationType) {
  const styles = {
    success: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      title: 'text-green-800',
      text: 'text-green-700',
      icon: 'text-green-400',
      closeButton: 'text-green-500 hover:bg-green-100 focus:ring-green-600',
    },
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      title: 'text-red-800',
      text: 'text-red-700',
      icon: 'text-red-400',
      closeButton: 'text-red-500 hover:bg-red-100 focus:ring-red-600',
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      title: 'text-blue-800',
      text: 'text-blue-700',
      icon: 'text-blue-400',
      closeButton: 'text-blue-500 hover:bg-blue-100 focus:ring-blue-600',
    },
    warning: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      title: 'text-yellow-800',
      text: 'text-yellow-700',
      icon: 'text-yellow-400',
      closeButton: 'text-yellow-500 hover:bg-yellow-100 focus:ring-yellow-600',
    },
  };

  return styles[type];
}

/**
 * 通知タイプに応じたアイコンパスを返す
 */
function getNotificationIcon(type: NotificationType): React.ReactElement {
  switch (type) {
    case 'success':
      return (
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
          clipRule="evenodd"
        />
      );
    case 'error':
      return (
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
          clipRule="evenodd"
        />
      );
    case 'info':
      return (
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
          clipRule="evenodd"
        />
      );
    case 'warning':
      return (
        <path
          fillRule="evenodd"
          d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
          clipRule="evenodd"
        />
      );
  }
}

/**
 * 通知バナーコンポーネント
 *
 * 機能:
 * - 成功メッセージ（緑色背景）
 * - エラーメッセージ（赤色背景）
 * - 情報メッセージ（青色背景）
 * - 警告メッセージ（黄色背景）
 * - アクセシビリティ対応（role="alert", aria-live="polite"）
 *
 * @example
 * ```tsx
 * <NotificationBanner
 *   type="success"
 *   title="確認完了"
 *   message="メールアドレスが確認されました。"
 * />
 * ```
 */
export const NotificationBanner: React.FC<NotificationBannerProps> = ({
  type,
  message,
  title,
  dismissible = false,
  onDismiss,
  className = '',
}) => {
  const styles = getNotificationStyles(type);

  return (
    <div
      className={`rounded-md border p-4 ${styles.bg} ${styles.border} ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex">
        {/* アイコン */}
        <div className="flex-shrink-0">
          <svg
            className={`h-5 w-5 ${styles.icon}`}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            {getNotificationIcon(type)}
          </svg>
        </div>

        {/* メッセージ */}
        <div className="ml-3 flex-1">
          {title && (
            <h3 className={`text-sm font-medium ${styles.title}`}>
              {title}
            </h3>
          )}
          <div className={`${title ? 'mt-2' : ''} text-sm ${styles.text}`}>
            <p>{message}</p>
          </div>
        </div>

        {/* 閉じるボタン */}
        {dismissible && onDismiss && (
          <div className="ml-auto pl-3">
            <button
              type="button"
              onClick={onDismiss}
              className={`inline-flex rounded-md p-1.5 ${styles.closeButton} focus:outline-none focus:ring-2 focus:ring-offset-2`}
              aria-label="通知を閉じる"
            >
              <span className="sr-only">閉じる</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
